import React from 'react';
import { Avatar, Box, Grid, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import { STEP_TYPE_NAMES } from 'constants/SparkSteps';
import {
  SparkEditableTitle,
  SparkScrollableGridContainer,
} from 'sharedComponents/UI';
import PromptFunctionsForm from '../forms/PromptFunctions/PromptFunctionsForm';
import { PluginsFormContainer } from '../forms/PluginFunctions';
import { MemoryFormContainer } from '../forms/MemoryForm';
import {
  useSparkStepByID,
  useUpdateSparkStep,
} from 'hooks/SparkBuilder/sparkStep';
import PromptFunctionsFormSkeleton from 'sharedComponents/Skeleton/PromptFunctionsFormSkeleton';
import EditableTitleSkeleton from 'sharedComponents/Skeleton/EditableTitleSkeleton';
import { useGetPromptFunctionsByID } from 'hooks/SparkBuilder/promptFunctions';
import { usePluginsByID } from 'hooks/SparkBuilder/pluginFunction';
import { useMemoryByID } from 'hooks/memory/useMemoryByID';
import { useSparkBuilderStore } from 'stores';

type CreateHeaderProps = {
  iconUrl: string | undefined;
  name: string;
  onTitleSave: (newTitle: string) => void;
  onClose: () => void;
};
const CreateHeader = ({
  name,
  iconUrl,
  onTitleSave,
  onClose,
}: CreateHeaderProps) => {
  return (
    <Box
      display='flex'
      alignItems='center'
      justifyContent='space-between'
      flexWrap='nowrap'
      width='100%'
    >
      <SparkEditableTitle
        containerStyle={{ width: '95%', maxWidth: '95%' }}
        startAdornment={
          <Avatar
            src={iconUrl}
            sx={{
              width: '30px',
              height: '30px',
            }}
          />
        }
        value={name}
        onTitleSave={onTitleSave}
        fullWidth={true}
        inputStyle={{
          fontFamily: 'Mont-semibold',
          fontSize: 24,
          maxWidth: 'inherit',
          paddingRight: '5px',
        }}
      />
      <IconButton
        aria-label='close'
        onClick={onClose}
        size='small'
        sx={{
          marginRight: -1,
        }}
      >
        <Close />
      </IconButton>
    </Box>
  );
};

type ContentGeneratorProps = {
  stepID: string | undefined;
  onTitleSave: (newTitle: string) => void;
  handleContinueClick: () => void;
  onClose: () => void;
};

const ContentGenerator = ({
  stepID,
  onTitleSave,
  handleContinueClick,
  onClose,
}: ContentGeneratorProps) => {
  const sparkStepTypeIdMap = useSparkBuilderStore(
    (store) => store.stepTypeIdMap
  );

  const sparkStep = useSparkBuilderStore((store) =>
    store.sparkStepsActions.getSparkStep(stepID || '')
  );

  if (!sparkStep) {
    return <></>;
  }

  const functionType =
    sparkStepTypeIdMap?.[sparkStep.attributes.sparkStepTypeID];
  const functionName = functionType?.attributes.key || '';
  const handleContinue = () => {
    handleContinueClick();
  };
  console.log('functionName', functionName);
  const header = (
    <CreateHeader
      onTitleSave={onTitleSave}
      iconUrl={functionType?.attributes.iconURL}
      name={sparkStep.attributes.name}
      onClose={onClose}
    />
  );

  const promptFunctionID = sparkStep?.attributes?.['promptFunctionID'];

  let content = <></>;
  switch (functionName) {
    case STEP_TYPE_NAMES.PROMPT:
      if (!promptFunctionID) {
        console.error('no prompt function id at sparkStepDrawerContent');
        return <></>;
      }
      content = (
        <PromptFunctionsForm
          promptFunctionID={promptFunctionID}
          handleContinue={handleContinue}
          key={promptFunctionID}
        />
      );
      break;

    case STEP_TYPE_NAMES.PLUGIN:
      const pluginFunctionID = sparkStep?.attributes?.['pluginFunctionID'];
      const pluginID = sparkStep?.attributes?.['pluginID'];
      content = (
        <PluginsFormContainer
          pluginFunctionID={pluginFunctionID}
          pluginID={pluginID}
          handleContinue={handleContinue}
          key={pluginFunctionID}
        />
      );
      break;

    case STEP_TYPE_NAMES.MEMORY:
      const memoryID = sparkStep?.attributes?.['memoryID'];
      if (!promptFunctionID) {
        console.error('no prompt function id at sparkStepDrawerContent');
        return <></>;
      }
      content = (
        <MemoryFormContainer
          promptFunctionID={promptFunctionID}
          memoryID={memoryID}
          handleContinue={handleContinue}
          key={memoryID}
        />
      );
      break;

    default:
      content = <></>;
      break;
  }
  return (
    <SparkScrollableGridContainer
      header={header}
      scrollableContainerStyle={{ padding: 0 }}
    >
      {content}
    </SparkScrollableGridContainer>
  );
};

type SparkStepDrawerContentProps = {
  sparkStepId: string | undefined;
  handleContinueClick: () => void;
  onClose: () => void;
};
function SparkStepDrawerContent({
  sparkStepId,
  handleContinueClick,
  onClose,
}: SparkStepDrawerContentProps) {
  const sparkID = useSparkBuilderStore((store) => store.sparkID);
  //do not remove from call as it saves data to store and everywhere else step is taken from store
  const { data: { data: sparkStep } = {}, isLoading: isSparkStepLoading } =
    useSparkStepByID(sparkStepId);
  const { mutateAsync: updateSparkStep } = useUpdateSparkStep(sparkID);
  const { isLoading: isPromptFunctionLoading } = useGetPromptFunctionsByID(
    sparkStep?.attributes.promptFunctionID
  );
  const { isLoading: isMemoryLoading } = useMemoryByID(
    sparkStep?.attributes.memoryID
  );
  const { isLoading: isPluginLoading } = usePluginsByID(
    sparkStep?.attributes.pluginID
  );

  const handleTitleSave = async (newTitle: string) => {
    updateSparkStep({ stepID: sparkStepId, name: newTitle });
  };

  const isLoading =
    isSparkStepLoading ||
    isPromptFunctionLoading ||
    isPluginLoading ||
    isMemoryLoading;

  if (isLoading) {
    return (
      <Box width='100%'>
        <Grid container height='4rem' alignItems='center'>
          <EditableTitleSkeleton showCircle={true} />
        </Grid>
        <Grid
          container
          justifyContent='center'
          alignItems='center'
          paddingTop={2}
        >
          <PromptFunctionsFormSkeleton />
        </Grid>
      </Box>
    );
  }

  return (
    <ContentGenerator
      stepID={sparkStep?.id}
      handleContinueClick={handleContinueClick}
      onTitleSave={handleTitleSave}
      onClose={onClose}
    />
  );
}

export default SparkStepDrawerContent;
